import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { AffiliateApplication, HoldAffiliateApplicationDto } from '../domain/affiliate-application-types';
import { affiliateApplicationService } from '../infra/affiliateApplicationService';
import { transformAffiliateApplication } from '../infra/mappers';

interface HoldAffiliateApplicationParams {
  projectId: string;
  applicationId: string;
  dto?: HoldAffiliateApplicationDto;
}

interface UseHoldAffiliateApplicationResult {
  holdApplication: (params: HoldAffiliateApplicationParams) => void;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
}

export const useHoldAffiliateApplication = (): UseHoldAffiliateApplicationResult => {
  const queryClient = useQueryClient();

  const {
    mutate,
    isPending,
    isError,
    isSuccess,
    error,
  }: UseMutationResult<AffiliateApplication, Error, HoldAffiliateApplicationParams> = useMutation({
    mutationFn: async ({ projectId, applicationId, dto }: HoldAffiliateApplicationParams) => {
      const response = await affiliateApplicationService.holdApplication(projectId, applicationId, dto);
      return transformAffiliateApplication(response);
    },
    onSuccess: (data, variables) => {
      toast.success('Application put on hold');
      queryClient.invalidateQueries({
        queryKey: ['affiliate-applications', variables.projectId],
      });
      queryClient.invalidateQueries({
        queryKey: ['affiliate-application', variables.projectId, variables.applicationId],
      });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to put application on hold');
    },
  });

  return {
    holdApplication: mutate,
    isLoading: isPending,
    isError,
    isSuccess,
    error,
  };
};
